// src/pages/SuccessPaymentBill.jsx

import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { styled } from '@mui/material/styles';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Divider,
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useLocation, useNavigate } from 'react-router-dom';
import Services from 'src/services/Services';
import { toast } from 'react-toastify';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import logo from '../assets/images/logo-form.png';
import moment from 'moment';
import useResponsive from '../hooks/useResponsive';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

// ----------------------------------------------------------------------
// Sol panel ve içerik alanı stilleri
const StyledRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const StyledSection = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 380,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  boxShadow: theme.customShadows?.card,
  backgroundColor: theme.palette.background.default,
}));

const StyledContent = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 680,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(12, 2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 2),
    maxWidth: '100%',
  },
}));

// ----------------------------------------------------------------------
export default function SuccessPaymentBill() {
  const navigate = useNavigate();
  const location = useLocation();
  const { invoice, tax, token } = location.state || {};
  const [loading, setLoading] = useState(false);
  const mdUp = useResponsive('up', 'md');

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

  const truncate2 = (num) => Math.floor((num || 0) * 100) / 100;

  const generatePdf = (item) => {
    const pageWidthPt = 595.28;
    const marginPt = 40 * 2;
    const contentWidth = pageWidthPt - marginPt;
    const PADDING = 8;
    const BOTTOM_SPACE = 30;

    const currency = item?.currency || 'TRY';

    // net hizmet tutarı (lineItems toplamı)
    const totalService =
      item?.lineItems?.reduce((sum, li) => sum + truncate2(li.tutar), 0) || 0;
    // toplam + KDV’li tutar
    const totalWithVat = truncate2(item?.price || 0);

    const invoiceData = {
      invoiceNumber: item?.invoiceID || '',
      invoiceNo: item?.invoiceID || '',
      taxNo: item?.taxNo || '',
      invoiceDate: item?.invoiceDate ? formatDate(item.invoiceDate) : '',
      paymentDate: item?.paymentDateTime
        ? formatDate(item.paymentDateTime)
        : '',
      fromName: item?.from.companyTitle || '',
      fromAddress: item?.from.address || '',
      fromPhone: item?.from.phone || '',
      toName: item?.to.companyTitle || '',
      toAddress: item?.to.address || '',
      toPhone: item?.to.phone || '',
      explanationTitle: item?.description || '',
      explanationDetail: item?.description || '',
      quantity: item?.lineItems?.length || 0,
      unitPrice:
        item?.lineItems?.[0]?.tutar?.toLocaleString('tr-TR', {
          style: 'currency',
          currency,
        }) || '',
      subtotal: totalService.toLocaleString('tr-TR', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      total: totalWithVat.toLocaleString('tr-TR', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      conversationID: item?.conversationID || '-',
    };

    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [40, 40, 40, 40],
      content: [
        // Logo & Başlık
        {
          columns: [
            { image: logo, width: 80 },
            {
              width: '*',
              stack: [
                {
                  text: 'AVM Lojistik A.Ş.',
                  fontSize: 12,
                  alignment: 'center',
                },
                {
                  text: 'ÖDEME ALINDISI',
                  fontSize: 18,
                  bold: true,
                  color: '#006366',
                  alignment: 'center',
                  margin: [0, 4, 0, 0],
                },
              ],
            },
          ],
          columnGap: 10,
          margin: [0, 0, 0, 15],
        },

        // Özet Kutusu 1
        {
          stack: [
            {
              canvas: [
                {
                  type: 'rect',
                  x: 0,
                  y: 0,
                  w: contentWidth,
                  h: 98,
                  r: 5,
                  lineColor: '#00B0BD',
                  lineWidth: 1,
                },
              ],
            },
            {
              table: {
                widths: ['auto', 'auto', '*'],
                body: [
                  [
                    { text: 'ÖDEME NUMARASI', bold: true, fontSize: 10 },
                    { text: ':', bold: true, fontSize: 10 },
                    { text: invoiceData.conversationID, fontSize: 10 },
                  ],
                  [
                    { text: 'FİRMA ÜNVANI', bold: true, fontSize: 10 },
                    { text: ':', bold: true, fontSize: 10 },
                    { text: invoiceData.fromName, fontSize: 10 },
                  ],
                  [
                    { text: 'VERGİ DAİRESİ VE NO', bold: true, fontSize: 10 },
                    { text: ':', bold: true, fontSize: 10 },
                    { text: invoiceData.taxNo, fontSize: 10 },
                  ],
                  [
                    { text: 'ÖDEME TARİHİ', bold: true, fontSize: 10 },
                    { text: ':', bold: true, fontSize: 10 },
                    { text: invoiceData.paymentDate, fontSize: 10 },
                  ],
                  [
                    {
                      text: 'GENEL TOPLAM (KARTTAN ÖDENEN)',
                      bold: true,
                      fontSize: 10,
                    },
                    { text: ':', bold: true, fontSize: 10 },
                    { text: invoiceData.total, fontSize: 10 },
                  ],
                ],
              },
              layout: 'noBorders',
              margin: [PADDING, -(98 - PADDING), PADDING, BOTTOM_SPACE],
            },
          ],
        },

        // Özet Kutusu 2
        {
          stack: [
            {
              canvas: [
                {
                  type: 'rect',
                  x: 0,
                  y: 0,
                  w: contentWidth,
                  h: 80,
                  r: 5,
                  lineColor: '#00B0BD',
                  lineWidth: 1,
                },
              ],
            },
            {
              table: {
                widths: ['*', '*'],
                body: [
                  [
                    { text: 'FATURA NUMARASI :', bold: true, fontSize: 10 },
                    {
                      text: 'TUTARI',
                      bold: true,
                      fontSize: 10,
                      alignment: 'right',
                    },
                  ],
                  [
                    { text: invoiceData.invoiceNo, fontSize: 10 },
                    {
                      text: invoiceData.subtotal,
                      fontSize: 10,
                      alignment: 'right',
                    },
                  ],
                  [
                    {
                      text: '',
                      italics: true,
                      fontSize: 8,
                      colSpan: 2,
                      alignment: 'center',
                    },
                    {},
                  ],
                  [
                    { text: 'TOPLAM TUTAR :', bold: true, fontSize: 10 },
                    {
                      text: invoiceData.total,
                      bold: true,
                      fontSize: 10,
                      alignment: 'right',
                    },
                  ],
                ],
              },
              layout: 'noBorders',
              margin: [PADDING, -(80 - PADDING), PADDING, BOTTOM_SPACE],
            },
          ],
        },

        // Özet Kutusu 3
        {
          stack: [
            {
              canvas: [
                {
                  type: 'rect',
                  x: 0,
                  y: 0,
                  w: contentWidth,
                  h: 60,
                  r: 5,
                  lineColor: '#00B0BD',
                  lineWidth: 1,
                },
              ],
            },
            {
              table: {
                widths: ['*', '*', '*'],
                body: [
                  [
                    { text: 'Tahsilat Şekli :', bold: true, fontSize: 10 },
                    { text: 'İşlem Türü :', bold: true, fontSize: 10 },
                    { text: 'Ödeme Şekli :', bold: true, fontSize: 10 },
                  ],
                  [
                    { text: invoiceData.explanationTitle, fontSize: 10 },
                    { text: invoiceData.explanationDetail, fontSize: 10 },
                    { text: 'KREDİ KARTI', fontSize: 10 },
                  ],
                ],
              },
              layout: 'noBorders',
              margin: [PADDING, -(60 - PADDING), PADDING, 0],
            },
          ],
        },
      ],
    };

    pdfMake
      .createPdf(docDefinition)
      .download(`Odeme_Alindi_${moment().format('DDMMYYYY_HHmm')}.pdf`);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await Services.GetInvoiceDetail(token, invoice, tax);
      if (!res.data.status) {
        toast.error(
          res.data.message === 'Invoice not found'
            ? 'Fatura bulunamadı'
            : 'Fatura bilgisi çekilemedi.'
        );
        return;
      }
      generatePdf(res.data.result);
    } catch (err) {
      console.error(err);
      toast.error('PDF oluşturulurken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Ödeme Başarılı | Mall Logistics Panel</title>
      </Helmet>

      <StyledRoot>
        {mdUp && (
          <StyledSection>
            <Box
              sx={{
                backgroundColor: '#103996',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <img src="/assets/logo.png" alt="Logo" />
            </Box>
            <Typography variant="h3" sx={{ px: 5, mt: 5, mb: 5 }}>
              Ödeme Başarılı
            </Typography>
            <img
              src="/assets/illustrations/login-ill.png"
              alt="login-ill"
              style={{ width: '100%' }}
            />
          </StyledSection>
        )}

        <StyledContent>
          <Card sx={{ maxWidth: 800, width: '100%', margin: 'auto' }}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <CheckCircleIcon
                sx={{
                  fontSize: 80,
                  color: 'success.main',
                  mb: 2,
                  display: 'block',
                  mx: 'auto',
                }}
              />
              <Typography variant="h5">
                #{invoice} no’lu faturanız ödenmiştir
              </Typography>
            </CardContent>

            <Divider />

            <CardActions sx={{ p: 2, flexDirection: 'column', gap: '15px' }}>
              <Button
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Makbuzu İndir'
                )}
              </Button>
              <Button
                onClick={() => navigate('/pay-bill')}
                variant="contained"
                color="primary"
                fullWidth
                style={{ marginLeft: '0px' }}
              >
                Yeni Bir Fatura Öde
              </Button>
            </CardActions>
          </Card>
        </StyledContent>
      </StyledRoot>
    </>
  );
}
